import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import DialerTreeConfig from "@/app/config";
import { Elements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCreatePaymentIntentMutation,
  useGetBillingOverviewQuery,
  useGetPaymentMethodsQuery,
} from "@/store/rest_api";
import React from "react";
import { CheckoutForm } from "@/components/pages/billing/CheckoutForm";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatCardBrand } from "@/util/payment";
import { CheckCircle2, CreditCard } from "lucide-react";
import { Separator } from "@radix-ui/react-select";
import { ExistingCardPayment } from "@/components/pages/billing/ExistingCardPayment";

const stripePromise = loadStripe(DialerTreeConfig.stripePublishableKey);

export function TopUp({ onSuccess }: { onSuccess?: () => void }) {
  const [amount, setAmount] = useState(50);
  const [paymentIntent, setPaymentIntent] = useState<{ clientSecret: string; paymentIntentId: string } | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"existing" | "new">("existing");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createPaymentIntent, { isLoading }] = useCreatePaymentIntentMutation();
  const { data: paymentMethods, isLoading: loadingPaymentMethods } = useGetPaymentMethodsQuery();
  const { refetch: refetchBilling } = useGetBillingOverviewQuery();

  const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;

  if (hasPaymentMethods && !selectedPaymentMethodId) {
    const defaultPM = paymentMethods.find((pm) => pm.isDefault);
    if (defaultPM) {
      setSelectedPaymentMethodId(defaultPM.id);
    } else {
      setSelectedPaymentMethodId(paymentMethods[0].id);
    }
  }

  const handleCreatePaymentIntent = async () => {
    if (amount < 5) {
      toast.error("Minimum amount is $5");
      return;
    }

    try {
      const result = await createPaymentIntent({ amount }).unwrap();
      setPaymentIntent({
        clientSecret: result.clientSecret,
        paymentIntentId: result.paymentIntentId,
      });
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to create payment intent");
    }
  };

  const handleSuccess = async () => {
    await refetchBilling();
    setShowSuccess(true);
    setTimeout(() => {
      setPaymentIntent(null);
      setAmount(50);
      setShowSuccess(false);
      onSuccess?.();
    }, 2000);
  };

  const options = {
    clientSecret: paymentIntent?.clientSecret || undefined,
    appearance: {
      theme: "stripe" as const,
    },
  };

  return (
    <div className="space-y-4">
      {showSuccess ? (
        <div className="flex flex-col items-center justify-center py-12">
          <CheckCircle2 className="h-24 w-24 text-green-500 animate-in zoom-in duration-300" />
          <p className="mt-4 text-lg font-semibold">Payment Successful!</p>
        </div>
      ) : !paymentIntent ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="amount">Amount (USD)</Label>
            <Input
              id="amount"
              type="number"
              min="5"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value))}
            />
          </div>
          <div className="flex gap-2">
            {[25, 50, 100, 250].map((preset) => (
              <Button key={preset} variant="outline" size="sm" onClick={() => setAmount(preset)} type="button">
                ${preset}
              </Button>
            ))}
          </div>

          {hasPaymentMethods && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label>Payment Method</Label>
                <RadioGroup
                  value={paymentMethod}
                  onValueChange={(value) => setPaymentMethod(value as "existing" | "new")}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="existing" id="existing" />
                    <Label htmlFor="existing" className="cursor-pointer">
                      Use existing card
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="new" id="new" />
                    <Label htmlFor="new" className="cursor-pointer">
                      Use new card
                    </Label>
                  </div>
                </RadioGroup>

                {paymentMethod === "existing" && (
                  <RadioGroup value={selectedPaymentMethodId || ""} onValueChange={setSelectedPaymentMethodId}>
                    <div className="space-y-2">
                      {loadingPaymentMethods ? (
                        <p className="text-sm text-muted-foreground">Loading cards...</p>
                      ) : (
                        paymentMethods?.map((pm) => (
                          <div
                            key={pm.id}
                            className="flex items-center space-x-2 rounded-lg border p-3 hover:bg-accent"
                          >
                            <RadioGroupItem value={pm.id} id={pm.id} />
                            <Label htmlFor={pm.id} className="flex items-center gap-2 cursor-pointer flex-1">
                              <CreditCard className="h-4 w-4 text-muted-foreground" />
                              <span>
                                {formatCardBrand(pm.card?.brand)} •••• {pm.card?.last4}
                              </span>
                              {pm.isDefault && <span className="text-xs text-muted-foreground">(Default)</span>}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </RadioGroup>
                )}
              </div>
            </>
          )}

          <Button
            onClick={handleCreatePaymentIntent}
            disabled={isLoading || (paymentMethod === "existing" && !selectedPaymentMethodId)}
            className="w-full"
          >
            {isLoading ? "Loading..." : "Continue to Payment"}
          </Button>
        </>
      ) : (
        <>
          {paymentMethod === "existing" && selectedPaymentMethodId ? (
            <ExistingCardPayment
              amount={amount}
              clientSecret={paymentIntent.clientSecret}
              selectedPaymentMethodId={selectedPaymentMethodId}
              onSuccess={handleSuccess}
            />
          ) : (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm amount={amount} clientSecret={paymentIntent.clientSecret} onSuccess={handleSuccess} />
            </Elements>
          )}
          <Button variant="outline" onClick={() => setPaymentIntent(null)} className="w-full">
            Cancel
          </Button>
        </>
      )}
    </div>
  );
}
